import { motion } from "framer-motion";
import { useState } from "react";

/**
 * RealJobsGallery — masonry grid of 12 real Bespoke job photos from the field.
 * Auto-pulled from user's Google Photos album. Click any tile to enlarge.
 */
const JOBS = Array.from({ length: 10 }, (_, i) => ({
  key: `job-${String(i + 1).padStart(2, "0")}`,
  src: `/brand/jobs/job-${String(i + 1).padStart(2, "0")}.jpg`,
  label: `Field · ${String(i + 1).padStart(3, "0")}`,
}));

export default function RealJobsGallery() {
  const [zoom, setZoom] = useState(null);

  return (
    <section
      className="py-24 md:py-32 relative overflow-hidden"
      style={{ background: "var(--bg)" }}
      data-testid="real-jobs-gallery"
    >
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(1200px 600px at 50% 0%, rgba(198,168,124,0.06), transparent 60%)",
      }} />

      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
          className="grid md:grid-cols-12 gap-8 mb-14"
        >
          <div className="md:col-span-3 overline pt-2">Field Gallery</div>
          <div className="md:col-span-9">
            <h2 className="font-display text-5xl md:text-7xl" style={{ letterSpacing: "-0.035em", lineHeight: 0.95 }}>
              Real jobs.{" "}
              <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>Not renderings.</span>
            </h2>
            <p className="mt-6 font-body max-w-2xl" style={{ color: "var(--text-2)", fontSize: 17, lineHeight: 1.65 }}>
              Every photo below is from a live Bespoke crew, on a real property, during a scheduled service. No stock. No AI. Click any tile to enlarge.
            </p>
          </div>
        </motion.div>

        {/* Masonry grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {JOBS.map((j, i) => (
            <motion.button
              key={j.key}
              onClick={() => setZoom(j)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.7, delay: (i % 4) * 0.05, ease: [0.16, 1, 0.3, 1] }}
              className="group relative overflow-hidden float-lift"
              style={{
                aspectRatio: "4 / 3",
                borderRadius: "16px",
                border: "1px solid var(--line-gold)",
              }}
              data-testid={`job-tile-${j.key}`}
              data-cursor="hover"
              data-cursor-label="Enlarge"
            >
              <img
                src={j.src}
                alt={j.label}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                style={{ filter: "brightness(0.85) contrast(1.05)" }}
                loading="lazy"
              />
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{
                background: "linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.7) 100%)",
              }} />
              <div className="absolute bottom-3 left-3 opacity-0 group-hover:opacity-100 transition-opacity duration-500 font-mono text-[9px] uppercase tracking-[0.32em]" style={{ color: "var(--gold-2)" }}>
                {j.label}
              </div>
            </motion.button>
          ))}
        </div>

        <div className="mt-10 text-center font-mono text-[9px] uppercase tracking-[0.42em]" style={{ color: "var(--text-3)" }}>
          More field work uploaded weekly · Client portfolios available on request
        </div>
      </div>

      {/* Zoom modal */}
      {zoom && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-6 cursor-zoom-out"
          onClick={() => setZoom(null)}
          style={{ background: "rgba(2,2,4,0.85)", backdropFilter: "blur(14px)" }}
          data-testid="job-zoom-overlay"
        >
          <img
            src={zoom.src}
            alt={zoom.label}
            className="max-h-[92vh] max-w-[92vw] object-contain"
            style={{ borderRadius: "20px", boxShadow: "0 60px 120px -30px rgba(0,0,0,0.9)" }}
          />
        </div>
      )}
    </section>
  );
}
